import {
    customerRelationshipOptions,
    getCustomerRelationShip,
    processIdentifiers,
} from "../../features/search/Filters/utility";
import { fixSearchStringForPostgres } from "../../shared/Utils";
import type { RootState } from "../types";
import type { SelectedJurisdiction } from "../../features/review/types/jurisdiction.types";
import type {
    SelectedTaxTypeMapping,
    TaxTypeDetail,
} from "../../features/review/types/multiTax.types";
import { buildMultiTaxRequestParams } from "../../features/search/Filters/mapMultiTaxToApiParams";

interface FormState {
    nameOrCode?: string;
    nameOrCodeWhileLoading?: string;
}

interface CompanyGroup {
    companyDivisionId?: number | string;
    companyIds?: number[] | string[];
}

interface ActiveFiltersState {
    nameOrCode?: string;
    invoiceNumber?: string;
    isVendorFilter?: boolean;
    certificateStatus?: string[];
    isMissingcertificates?: boolean;
    missingExposureZones?: string[];
    customerRelationShip?: string[];
    isDuplicateRelation?: boolean;
    isCertificateImage?: number | boolean;
    isCertActive?: number | string;
    isPurchaseOrder?: boolean;
    isMissingEmailAddress?: boolean;
    customerCreatedDateAfter?: string | null;
    customerCreatedDateBefore?: string | null;
    customerModifiedDateAfter?: string | null;
    customerModifiedDateBefore?: string | null;
    certificateCreatedDateAfter?: string | null;
    certificateCreatedDateBefore?: string | null;
    certificateModifiedDateAfter?: string | null;
    certificateModifiedDateBefore?: string | null;
    certificateEffectiveDateAfter?: string | null;
    certificateEffectiveDateBefore?: string | null;
    certificateExpirationDateAfter?: string | null;
    certificateExpirationDateBefore?: string | null;
    certificateValidatedDateAfter?: string | null;
    certificateValidatedDateBefore?: string | null;
    certificateRegions?: string[];
    customerRegions?: string[];
    exemptReasons?: string[];
    invalidReasons?: string[];
    customerCustomFields?: Record<string, unknown>;
    alternateId?: string;
    customersLabels?: number[] | string[];
    certificatesLabels?: number[] | string[];
    excludeCertificateLabels?: number[] | string[];
    companyGroup?: CompanyGroup;
    excludeCustomerLabels?: number[] | string[];
    city?: string;
    zip?: string;
    address?: string;
    phone?: string;
    federalTaxID?: string;
    email?: string;
    customerExposures?: string[];
    certificateIds?: string;
    customerCodes?: string;
    certificateCustomFields?: Record<string, unknown>;
    localJurisdictions?: SelectedJurisdiction[];
    multiTaxTypes?: string[];
    multiTaxSubTypes?: string[];
    multiTaxJurisdictions?: SelectedTaxTypeMapping[];
    overrideExemptReasons?: Array<string | number>;
    overrideRegions?: Array<string | number>;
    overrideCreatedDateAfter?: string | null;
    overrideCreatedDateBefore?: string | null;
    overrideEffectiveDateAfter?: string | null;
    overrideEffectiveDateBefore?: string | null;
    overrideExpirationDateAfter?: string | null;
    overrideExpirationDateBefore?: string | null;
    overrideLicenseNumber?: string;
    isOverrideActive?: number | string;
}

interface CustomerFiltersDataReturn {
    pageSize: number;
    pageNumber: number;
    selectedColumns: string[];
    orderBy: string;
    orderByDirection: "ASC" | "DESC";
    includes: string;
    nameOrCode: string;
    purchaseOrderNumber?: string;
    missing?: boolean;
    missingExposureZones?: string[];
    isBillTo?: boolean | null;
    isShipTo?: boolean | null;
    isDuplicate?: boolean;
    certificateImageExists?: number | boolean;
    activeCertificate?: number | string;
    isSingle?: boolean;
    returnEmptyEmailRecords?: boolean;
    isVendor?: boolean;
    customerCreatedFrom?: string | null;
    customerCreatedTo?: string | null;
    customerModifiedFrom?: string | null;
    customerModifiedTo?: string | null;
    certificateCreatedFrom?: string | null;
    certificateCreatedTo?: string | null;
    certificateModifiedFrom?: string | null;
    certificateModifiedTo?: string | null;
    certificateEffectiveFrom?: string | null;
    certificateEffectiveTo?: string | null;
    certificateExpirationFrom?: string | null;
    certificateExpirationTo?: string | null;
    certificateValidatedFrom?: string | null;
    certificateValidatedTo?: string | null;
    status?: string[];
    exposureZones?: string[];
    customerRegions?: string[];
    exemptionReasons?: string[];
    invalidReasons?: string[];
    customerCustomFields?: Record<string, unknown>;
    certificateCustomFields?: Record<string, unknown>;
    alternateId?: string;
    customersLabels?: number[] | string[];
    certificatesLabels?: number[] | string[];
    excludeCertificateLabels?: number[] | string[];
    companyDivisionId?: number | string;
    companyIds?: number[] | string[];
    excludeCustomerLabels?: number[] | string[];
    city?: string;
    zip?: string;
    address?: string;
    phoneNumber?: string;
    feinNumber?: string;
    emailAddress?: string;
    customerExposures?: string[];
    certificateIds?: number[];
    customerCodes?: string[];
    /** Matches API CustomerFilterModel.CertificateJurisdictionCodes (empty list when unset). */
    certificateJurisdictionCodes?: string[];
    /** Multi-tax "Tax type / Tax subtype" filter (source_mapping_id ids; -1 = Sales & Use Tax). */
    taxSubTaxMappingIds?: number[];
    /** Exemption-override filters (manual_taxcode_changes table). */
    overrideExemptionReasons?: Array<string | number>;
    overrideExposureZones?: Array<string | number>;
    overrideCreatedFrom?: string | null;
    overrideCreatedTo?: string | null;
    overrideEffectiveFrom?: string | null;
    overrideEffectiveTo?: string | null;
    overrideExpirationFrom?: string | null;
    overrideExpirationTo?: string | null;
    overrideLicenseNumber?: string;
    isOverrideActive?: boolean | null;
    avalaraUserId: string | number;
    avalaraUserRole: string;
    avaTaxAccountId: string | number;
    avaTaxCompanyId: string | number;
}

// Helper function to get search value
// Priority: nameOrCodeWhileLoading > activeFiltersState.nameOrCode > formState.nameOrCode
// This matches the logic used in fetchCustomersAPI for /customers/search
const getSearchValue = (formState: FormState, activeFiltersState: ActiveFiltersState): string => {
    const { nameOrCode, nameOrCodeWhileLoading } = formState;
    if (typeof nameOrCodeWhileLoading === "string" && nameOrCodeWhileLoading !== "") {
        return nameOrCodeWhileLoading;
    }
    const activeNameOrCode = activeFiltersState?.nameOrCode;
    if (typeof activeNameOrCode === "string" && activeNameOrCode !== "") {
        return activeNameOrCode;
    }
    return nameOrCode || "";
};

function customerFiltersData(
    selectedColumns: string[],
    getState: () => RootState,
    pageSize: number = 20,
    pageNumber: number = 0
): CustomerFiltersDataReturn {
    const state = getState();
    const { userId, accountId, role, activeCompany } = state.session as {
        userId: string | number;
        accountId: string | number;
        role: string;
        activeCompany: { id: string | number };
    };
    const { formState, customerFiltersState, vendorFiltersState, sortColumn } = state.customer as {
        formState: FormState;
        customerFiltersState: ActiveFiltersState;
        vendorFiltersState: ActiveFiltersState;
        sortColumn: [string, boolean];
    };

    // Determine which filter state to use based on isVendorFilter flag
    const activeFiltersState: ActiveFiltersState = vendorFiltersState.isVendorFilter
        ? vendorFiltersState
        : customerFiltersState;

    const searchVal = getSearchValue(formState, activeFiltersState);

    // Multi-tax params: deduped certificateJurisdictionCodes (SUT local jurisdictions + non-SUT
    // multi-tax codes) and the flat taxSubTaxMappingIds list. Shared with the live search
    // (fetchCustomersAPI) and bulk-delete paths so bulk operations act on the same filtered set.
    const taxTypeDetails = (state.certificate as { taxTypeDetails?: TaxTypeDetail[] } | undefined)
        ?.taxTypeDetails;
    const { certificateJurisdictionCodes, taxSubTaxMappingIds } = buildMultiTaxRequestParams(
        activeFiltersState,
        taxTypeDetails
    );

    return {
        // Pagination and sorting
        pageSize,
        pageNumber,
        selectedColumns,
        orderBy: sortColumn[0],
        orderByDirection: sortColumn[1] ? "DESC" : "ASC",
        includes: "",

        // Search and filters
        nameOrCode: (fixSearchStringForPostgres(searchVal) as string | undefined) ?? "",
        purchaseOrderNumber: activeFiltersState.invoiceNumber,
        missing:
            activeFiltersState.isVendorFilter &&
            activeFiltersState?.certificateStatus?.includes("Missing")
                ? true
                : activeFiltersState.isMissingcertificates,
        missingExposureZones: activeFiltersState.missingExposureZones,
        isBillTo: getCustomerRelationShip(
            activeFiltersState.customerRelationShip,
            customerRelationshipOptions.value.isBillTo
        ),
        isShipTo: getCustomerRelationShip(
            activeFiltersState.customerRelationShip,
            customerRelationshipOptions.value.isShipTo
        ),
        isDuplicate: activeFiltersState.isDuplicateRelation,
        certificateImageExists: activeFiltersState.isCertificateImage,
        activeCertificate: activeFiltersState.isCertActive,
        isSingle: activeFiltersState.isPurchaseOrder,
        returnEmptyEmailRecords: activeFiltersState.isMissingEmailAddress,
        isVendor: activeFiltersState.isVendorFilter,

        // Date ranges
        customerCreatedFrom: activeFiltersState.customerCreatedDateAfter || null,
        customerCreatedTo: activeFiltersState.customerCreatedDateBefore || null,
        customerModifiedFrom: activeFiltersState.customerModifiedDateAfter || null,
        customerModifiedTo: activeFiltersState.customerModifiedDateBefore || null,
        certificateCreatedFrom: activeFiltersState.certificateCreatedDateAfter || null,
        certificateCreatedTo: activeFiltersState.certificateCreatedDateBefore || null,
        certificateModifiedFrom: activeFiltersState.certificateModifiedDateAfter || null,
        certificateModifiedTo: activeFiltersState.certificateModifiedDateBefore || null,
        certificateEffectiveFrom: activeFiltersState.certificateEffectiveDateAfter || null,
        certificateEffectiveTo: activeFiltersState.certificateEffectiveDateBefore || null,
        certificateExpirationFrom: activeFiltersState.certificateExpirationDateAfter || null,
        certificateExpirationTo: activeFiltersState.certificateExpirationDateBefore || null,
        certificateValidatedFrom: activeFiltersState.certificateValidatedDateAfter || null,
        certificateValidatedTo: activeFiltersState.certificateValidatedDateBefore || null,

        // Status and categories
        status:
            activeFiltersState.isVendorFilter &&
            activeFiltersState?.certificateStatus?.includes("Missing")
                ? activeFiltersState?.certificateStatus?.filter(status => {
                      return status !== "Missing";
                  })
                : activeFiltersState.certificateStatus,
        exposureZones: activeFiltersState.certificateRegions,
        customerRegions: activeFiltersState.customerRegions,
        exemptionReasons: activeFiltersState.exemptReasons,
        invalidReasons: activeFiltersState.invalidReasons,
        customerCustomFields: activeFiltersState.customerCustomFields,
        certificateCustomFields: activeFiltersState.certificateCustomFields,
        alternateId: activeFiltersState.alternateId,
        customersLabels: activeFiltersState.customersLabels,
        certificatesLabels: activeFiltersState.certificatesLabels,
        excludeCertificateLabels: activeFiltersState.excludeCertificateLabels,
        companyDivisionId: activeFiltersState.companyGroup?.companyDivisionId,
        companyIds: activeFiltersState.companyGroup?.companyIds,
        excludeCustomerLabels: activeFiltersState.excludeCustomerLabels,
        city: activeFiltersState.city,
        zip: activeFiltersState.zip,
        address: activeFiltersState.address,
        phoneNumber: activeFiltersState.phone,
        feinNumber: activeFiltersState.federalTaxID,
        emailAddress: activeFiltersState.email,
        customerExposures: activeFiltersState.customerExposures,
        certificateJurisdictionCodes,
        taxSubTaxMappingIds,

        // Exemption overrides (manual_taxcode_changes)
        overrideExemptionReasons: activeFiltersState.overrideExemptReasons,
        overrideExposureZones: activeFiltersState.overrideRegions,
        overrideCreatedFrom: activeFiltersState.overrideCreatedDateAfter || null,
        overrideCreatedTo: activeFiltersState.overrideCreatedDateBefore || null,
        overrideEffectiveFrom: activeFiltersState.overrideEffectiveDateAfter || null,
        overrideEffectiveTo: activeFiltersState.overrideEffectiveDateBefore || null,
        overrideExpirationFrom: activeFiltersState.overrideExpirationDateAfter || null,
        overrideExpirationTo: activeFiltersState.overrideExpirationDateBefore || null,
        overrideLicenseNumber: activeFiltersState.overrideLicenseNumber,
        isOverrideActive:
            activeFiltersState.isOverrideActive === "1"
                ? true
                : activeFiltersState.isOverrideActive === "2"
                  ? false
                  : null,

        // Identifiers (exclude 0/NaN from blank or invalid lines when sending)
        certificateIds: processIdentifiers(activeFiltersState.certificateIds, (v: string) =>
            Number(fixSearchStringForPostgres(v))
        ).filter((id): id is number => id !== 0 && !Number.isNaN(id)),
        customerCodes: processIdentifiers(
            activeFiltersState.customerCodes,
            fixSearchStringForPostgres
        ),

        // User info
        avalaraUserId: userId,
        avalaraUserRole: role,
        avaTaxAccountId: accountId,
        avaTaxCompanyId: activeCompany.id,
    };
}

export default customerFiltersData;
